/**
 * The automation tab's reads, in order: the newest one always wins, and the
 * poll never starts a read while one is running.
 *
 * Two ways a plain "count the answers and drop the old ones" goes wrong. A
 * status read slower than the 15 s poll gets overtaken by the next poll before
 * it lands, and so does every read after it: the page never draws and the
 * server gets a heavy read every 15 s. And a switch that awaits a refresh can
 * have its answer dropped by a poll that started meanwhile, so the switch flips
 * back until the poll lands. So the poll skips while a read runs, and a refresh
 * (after an action) always starts its own read, whose answer is the one used.
 *
 * React-free, so it runs under plain node; `useAiAutomation.ts` holds one.
 */

export type ReadResult<T> = { ok: true; value: T } | { ok: false };

export interface StatusReader {
  /** Read now, whatever runs; resolves once this read's answer was applied or dropped. */
  refresh(): Promise<void>;
  /** Read unless a read is already running. */
  poll(): Promise<void>;
  /** A read is running. */
  busy(): boolean;
}

export function createStatusReader<T>(read: () => Promise<T>, apply: (result: ReadResult<T>) => void): StatusReader {
  let latest = 0;
  let running = 0;

  async function run(): Promise<void> {
    const id = ++latest;
    running += 1;
    try {
      const value = await read();
      if (id === latest) apply({ ok: true, value });
    } catch {
      if (id === latest) apply({ ok: false });
    } finally {
      running -= 1;
    }
  }

  return {
    refresh: run,
    poll: () => (running > 0 ? Promise.resolve() : run()),
    busy: () => running > 0,
  };
}
